// Betrieb und Standort wechseln (design-system §3.4, §3.8). Der Standort ist ein Arbeitskontext: Er füllt Filter
// und Formulare vor und ändert nicht, was eine geöffnete Seite zeigt. Der Betrieb wechselt die ganze Sitzung.
// Betreiber (freigegebenes Konto mit Adresse in MERXO_SUPPORT_OPERATORS) sehen zusätzlich den Abschnitt
// „Support-Zugang“ mit allen übrigen Betrieben
// (docs/anmeldung/SPEZIFIKATION.md §18) – Quelle ist allein `session.support`.
import { ChevronsUpDown, MapPin } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toastError } from '../api/errors'
import { isWorkLocation, LOCATION_KIND_LABELS } from '../api/permissions'
import { useSession } from '../api/session'
import type { SessionLocation, SessionPayload } from '../api/types'
import { cn } from '../lib/cn'
import { useHotkey } from '../lib/hotkeys'
import { selectTenant, setActiveLocation } from '../stores/session'
import { Badge } from '../ui/Badge'
import { DropdownMenu, type MenuEntry } from '../ui/DropdownMenu'
import { Logo } from '../ui/Logo'
import { toast } from '../ui/Toast'
import { Tooltip } from '../ui/Tooltip'
import { DEMO_LABEL } from './DemoMark'

const KIND_ORDER = ['warehouse', 'store', 'vehicle']

/** Menüeinträge des Standort-Schalters: „Alle Standorte“, dann nach Art gruppiert. */
export function locationMenu(
  locations: readonly SessionLocation[],
  activeId: string | null,
  onSelect: (locationId: string | null) => void,
): MenuEntry[] {
  const work = locations.filter(isWorkLocation)
  const entries: MenuEntry[] = []
  if (work.length > 1) {
    entries.push({
      type: 'radio',
      id: 'all',
      label: 'Alle Standorte',
      checked: activeId === null,
      onSelect: () => onSelect(null),
    })
  }
  const kinds = [...new Set(work.map((location) => location.kind))].sort(
    (a, b) => KIND_ORDER.indexOf(a) - KIND_ORDER.indexOf(b),
  )
  for (const kind of kinds) {
    entries.push({ type: 'label', id: `kind:${kind}`, label: LOCATION_KIND_LABELS[kind] ?? kind })
    for (const location of work.filter((entry) => entry.kind === kind)) {
      entries.push({
        type: 'radio',
        id: location.id,
        label: location.name,
        checked: activeId === location.id,
        onSelect: () => onSelect(location.id),
        meta: <span className="code-id text-fg-subtle">{location.code}</span>,
      })
    }
  }
  return entries
}

export async function switchLocation(locationId: string | null, locations: readonly SessionLocation[]): Promise<void> {
  try {
    await setActiveLocation(locationId)
    const location = locations.find((entry) => entry.id === locationId)
    toast.show({
      id: 'location',
      title: location ? `Arbeitsstandort: ${location.code} ${location.name}` : 'Arbeitsstandort: alle Standorte',
      description: 'Geöffnete Listen behalten ihren Filter, neue Vorgänge beginnen hier.',
    })
  } catch (error) {
    toastError(error)
  }
}

const trigger =
  'flex w-full min-w-0 items-center gap-2 rounded-sm text-left text-dense transition-colors duration-(--duration-instant) touch:text-touch'

export interface SwitcherProps {
  /** nur das Kürzel zeigen (eingeklappte Sidebar) */
  collapsed?: boolean
  className?: string
}

export function LocationSwitcher({ collapsed, className }: SwitcherProps) {
  const session = useSession()
  const [open, setOpen] = useState(false)
  const locations = session?.locations ?? []
  const work = locations.filter(isWorkLocation)
  const active = locations.find((location) => location.id === session?.activeLocationId) ?? null
  const interactive = work.length > 1
  useHotkey('o', () => setOpen(true), { enabled: interactive })
  if (work.length === 0) return null

  const label = active ? active.name : 'Alle Standorte'
  const face = (
    <>
      {active ? (
        <Badge size="sm" className="shrink-0">
          {active.code}
        </Badge>
      ) : (
        <MapPin className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} aria-hidden />
      )}
      {!collapsed && <span className="min-w-0 flex-1 truncate-line text-fg">{label}</span>}
      {!collapsed && interactive && (
        <ChevronsUpDown className="size-4 shrink-0 text-fg-subtle" strokeWidth={1.75} aria-hidden />
      )}
    </>
  )
  const frame = cn(
    trigger,
    'h-8 border border-line bg-surface shadow-xs touch:h-11',
    collapsed ? 'justify-center px-0' : 'px-2',
    className,
  )
  if (!interactive) {
    return (
      <div className={frame} data-testid="location-switcher" aria-label={`Standort: ${label}`}>
        {face}
      </div>
    )
  }
  const button = (
    <button
      type="button"
      className={cn(frame, 'hover:bg-hover')}
      data-testid="location-switcher"
      aria-label={`Standort wechseln, aktuell: ${label}`}
    >
      {face}
    </button>
  )
  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      trigger={button}
      width={collapsed ? 'sm' : 'trigger'}
      side={collapsed ? 'right' : 'bottom'}
      align="start"
      items={locationMenu(locations, session?.activeLocationId ?? null, (id) => void switchLocation(id, locations))}
    />
  )
}

/** Überschrift des Abschnitts für Betreiber */
export const SUPPORT_SECTION_LABEL = 'Support-Zugang'

/**
 * Einträge „Support-Zugang“ für den Betriebswechsler (§18): alle Betriebe ohne eigene Mitgliedschaft. Ohne
 * Zwei-Faktor-Anmeldung (bzw. Sitzung ohne Code) steht der Grund als gesperrte Zeile davor und die Betriebe sind
 * gesperrt; Betriebe, die den Zugang abgeschaltet haben, sind gesperrt mit Vermerk. Leer für alle anderen.
 */
export function supportMenu(
  session: Pick<SessionPayload, 'support' | 'tenant'> | null | undefined,
  onSelect: (tenantId: string) => void,
  options: { separator?: boolean } = {},
): MenuEntry[] {
  const support = session?.support
  if (!support || support.tenants.length === 0) return []
  const entries: MenuEntry[] = [
    ...(options.separator ? [{ type: 'separator', id: 'support-separator' } as MenuEntry] : []),
    { type: 'label', id: 'support-label', label: SUPPORT_SECTION_LABEL },
  ]
  if (!support.available) {
    entries.push({
      id: 'support-unavailable',
      label: 'Nicht verfügbar',
      disabled: true,
      disabledReason: support.reason ?? undefined,
    })
  }
  for (const entry of support.tenants) {
    entries.push({
      type: 'radio',
      id: `support:${entry.tenantId}`,
      label: entry.tenantName,
      checked: support.active && entry.tenantId === session?.tenant?.id,
      onSelect: () => onSelect(entry.tenantId),
      disabled: !support.available || !entry.allowed,
      meta: !entry.allowed ? 'gesperrt' : entry.demo ? 'Demo' : undefined,
    })
  }
  return entries
}

export function TenantSwitcher({ collapsed, className }: SwitcherProps) {
  const session = useSession()
  const navigate = useNavigate()
  const tenant = session?.tenant
  const memberships = session?.memberships ?? []
  const supportTenants = session?.support?.tenants ?? []
  const interactive = memberships.length > 1 || supportTenants.length > 0
  if (!tenant) return null

  const mark = tenant.logoUrl ? (
    <img src={tenant.logoUrl} alt="" className="size-5 shrink-0 rounded-xs object-contain" />
  ) : (
    <Logo size={20} title={null} className="shrink-0" />
  )
  const face = (
    <>
      {mark}
      {!collapsed && <span className="min-w-0 flex-1 truncate-line font-semibold text-fg">{tenant.name}</span>}
      {!collapsed && interactive && (
        <ChevronsUpDown className="size-4 shrink-0 text-fg-subtle" strokeWidth={1.75} aria-hidden />
      )}
    </>
  )
  const frame = cn(trigger, 'h-9 touch:h-11', collapsed ? 'justify-center px-0' : 'px-2', className)
  if (!interactive) {
    const plain = (
      <div className={frame} data-testid="tenant-name">
        {face}
      </div>
    )
    return collapsed ? (
      <Tooltip content={tenant.demo ? `${tenant.name} · ${DEMO_LABEL}` : tenant.name} side="right">
        {plain}
      </Tooltip>
    ) : (
      plain
    )
  }
  const change = async (tenantId: string): Promise<void> => {
    if (tenantId === tenant.id) return
    try {
      await selectTenant(tenantId)
      void navigate('/')
    } catch (error) {
      toastError(error)
    }
  }
  return (
    <DropdownMenu
      trigger={
        <button
          type="button"
          className={cn(frame, 'hover:bg-nav-hover')}
          data-testid="tenant-switcher"
          aria-label={`Betrieb wechseln, aktuell: ${tenant.name}`}
        >
          {face}
        </button>
      }
      width={collapsed ? 'sm' : 'trigger'}
      side={collapsed ? 'right' : 'bottom'}
      align="start"
      items={[
        ...(memberships.length > 0 ? [{ type: 'label', id: 'label', label: 'Betrieb' } as MenuEntry] : []),
        ...memberships.map((membership): MenuEntry => ({
          type: 'radio',
          id: membership.tenantId,
          label: membership.tenantName,
          checked: membership.tenantId === tenant.id,
          onSelect: () => void change(membership.tenantId),
        })),
        ...supportMenu(session, (tenantId) => void change(tenantId), { separator: memberships.length > 0 }),
      ]}
    />
  )
}
